import { OwcTypography, OwcProgressBar } from '@one/react';
import Banner from '../../../../components/Common/Banner';
import StatusChip from '../../../../components/Common/StatusChip';
import ConfirmInput from '../../../../components/Common/ConfirmInput';
import OrphanAck from '../../../../components/Common/OrphanAck';
import DeprecationPlanTable from './DeprecationPlanTable';
import { formatReleaseVersion } from '../../../../utils/release/deprecationPlan';
import styles from './DeprecateReleaseModal.module.css';

/**
 * Step 2 of the deprecate flow: impact summary, operation-mode hint,
 * release-level outcome notice, the entity table, selection-drift warning, and
 * the type-to-confirm guard.
 *
 * Presentational: all state and handlers come from the orchestrator.
 */
export default function DeprecationReview({
  plan,
  releaseVersion,
  selectedPks,
  filteredEntities,
  orphanCount,
  activeTab,
  search,
  showOrphansOnly,
  confirmVersion,
  orphanAck,
  orphanAckRequired,
  selectionDrifted,
  errorMessage,
  isLoading,
  onTabChange,
  onSearchChange,
  onOrphansToggle,
  onToggleVisible,
  onConfirmChange,
  onOrphanAckChange,
}) {
  const affected = plan?.affectedEntities ?? 0;
  const selectedCount = selectedPks.length;

  return (
    <div className={styles.reviewStep}>
      <div className={styles.summaryRow}>
        <OwcTypography variant="title5">
          Impact: {affected} entit{affected === 1 ? 'y' : 'ies'}
        </OwcTypography>
        {orphanCount > 0 && (
          <StatusChip tone="error">
            {orphanCount} orphan risk{orphanCount === 1 ? '' : 's'}
          </StatusChip>
        )}
      </div>

      {/* Operation 1 (untag all) vs Operation 2 (inactivate selected) hint */}
      <div
        className={`${styles.modeHint} ${
          selectedCount > 0 ? styles.modeHintOp2 : styles.modeHintOp1
        }`}
      >
        {selectedCount > 0 ? (
          <>
            <strong>{selectedCount}</strong> entit{selectedCount === 1 ? 'y' : 'ies'} selected to{' '}
            <strong className={styles.danger}>inactivate</strong>; the rest will be untagged.
          </>
        ) : (
          <>
            No entities selected — the release will be deprecated and{' '}
            <strong>all entities untagged</strong> (none inactivated). Select rows below to
            inactivate specific entities.
          </>
        )}
      </div>

      {/* Release-level outcome (promotion / resume). Deprecate plans have no
          warnings list — that is delete-only. */}
      {(plan?.releasePromoteTo || plan?.isResuming) && (
        <div className={styles.outcomeNotice}>
          {plan.releasePromoteTo && (
            <div>
              ℹ️ Release <strong>{formatReleaseVersion(plan.releasePromoteTo)}</strong> will be
              promoted to latest.
            </div>
          )}
          {plan.isResuming && (
            <div>
              ⚠️ This release is already in a <strong>Deprecating</strong> state; executing will
              resume the previous operation.
            </div>
          )}
        </div>
      )}

      <DeprecationPlanTable
        visibleEntities={filteredEntities}
        selectedPks={selectedPks}
        releaseVersion={releaseVersion}
        activeTab={activeTab}
        search={search}
        showOrphansOnly={showOrphansOnly}
        orphanCount={orphanCount}
        onTabChange={onTabChange}
        onSearchChange={onSearchChange}
        onOrphansToggle={onOrphansToggle}
        onToggleVisible={onToggleVisible}
      />

      {selectionDrifted && (
        <Banner variant="warning">
          Your entity selection changed. Re-run the analysis so the plan above matches what will be
          executed.
        </Banner>
      )}

      {orphanAckRequired && (
        <OrphanAck count={orphanCount} checked={orphanAck} onChange={onOrphanAckChange} />
      )}

      <ConfirmInput
        title="Confirm deprecation"
        confirmText={releaseVersion}
        value={confirmVersion}
        onChange={onConfirmChange}
      />

      {errorMessage && <Banner variant="error">{errorMessage}</Banner>}

      {isLoading && (
        <div className={styles.progress}>
          <OwcProgressBar size="medium" color="error" indeterminate />
          <div className={styles.progressLabel}>Deprecating release, please wait...</div>
        </div>
      )}
    </div>
  );
}
